import React from 'react';
import CrudPage from '../components/CrudPage';

export const Engineering = () => (
  <CrudPage overline="Engineering & Design" title="Drawing Register"
    subtitle="Version-controlled register of all engineering drawings and design documents."
    endpoint="drawings"
    fields={[
      { key: 'drawing_no', label: 'Drawing No' },
      { key: 'title', label: 'Title', full: true },
      { key: 'discipline', label: 'Discipline', type: 'select', options: ['Civil','Structural','Electrical','Plumbing','HVAC','Architectural'] },
      { key: 'revision', label: 'Revision' },
      { key: 'status', label: 'Status', type: 'select', options: ['IFC','IFR','Superseded'] },
      { key: 'issued_by', label: 'Issued By' },
    ]}
    columns={[
      { key: 'drawing_no', label: 'No' }, { key: 'title', label: 'Title' },
      { key: 'discipline', label: 'Discipline' }, { key: 'revision', label: 'Rev' },
      { key: 'status', label: 'Status', type: 'status' }, { key: 'issued_by', label: 'Issued By' },
    ]}
  />
);

export const Inventory = () => (
  <CrudPage overline="Material & Inventory" title="Site Store"
    subtitle="Real-time stock position across all site stores with reorder alerts."
    endpoint="inventory-items"
    fields={[
      { key: 'code', label: 'Item Code' },
      { key: 'name', label: 'Item Name', full: true },
      { key: 'unit', label: 'UoM' },
      { key: 'stock', label: 'Current Stock', type: 'number' },
      { key: 'reorder_level', label: 'Reorder Level', type: 'number' },
    ]}
    columns={[
      { key: 'code', label: 'Code' }, { key: 'name', label: 'Item' },
      { key: 'unit', label: 'UoM' }, { key: 'stock', label: 'Stock' },
      { key: 'reorder_level', label: 'Reorder Lvl' },
    ]}
  />
);

export const Equipment = () => (
  <CrudPage overline="Plant & Machinery" title="Equipment Register"
    subtitle="Owned and hired equipment — deployment, utilisation and maintenance."
    endpoint="equipment"
    fields={[
      { key: 'eq_id', label: 'Equipment ID' },
      { key: 'type', label: 'Type', type: 'select', options: ['Excavator','Tower Crane','Transit Mixer','DG Set','Loader','Grader','Concrete Pump'] },
      { key: 'make', label: 'Make / Model' },
      { key: 'rate', label: 'Rate / day (₹)', type: 'number' },
      { key: 'location', label: 'Current Location' },
      { key: 'status', label: 'Status', type: 'select', options: ['Working','Idle','Maintenance','Breakdown'] },
    ]}
    columns={[
      { key: 'eq_id', label: 'ID' }, { key: 'type', label: 'Type' },
      { key: 'make', label: 'Make' }, { key: 'rate', label: 'Rate', type: 'currency' },
      { key: 'location', label: 'Location' },
      { key: 'status', label: 'Status', type: 'status' },
    ]}
  />
);

const _SubcontractorsLegacy = () => (
  <CrudPage overline="Subcontractor" title="Subcontractor Master"
    subtitle="Empanelled subcontractors with work orders and RA bill tracking."
    endpoint="subcontractors"
    fields={[
      { key: 'name', label: 'Name', full: true },
      { key: 'specialty', label: 'Specialty', type: 'select', options: ['Civil','MEP','Painting','Plumbing','Electrical','Steel'] },
      { key: 'gst', label: 'GSTIN' },
      { key: 'rating', label: 'Rating', type: 'number' },
    ]}
    columns={[
      { key: 'name', label: 'Name' }, { key: 'specialty', label: 'Specialty' },
      { key: 'gst', label: 'GSTIN' }, { key: 'rating', label: 'Rating' },
    ]}
  />
);

export const HSE = () => (
  <CrudPage overline="Health · Safety · Environment" title="HSE Incident Report"
    subtitle="Near misses, first-aid, LTI and reportable incidents across all sites."
    endpoint="incidents"
    formId="hse_incident"
    fields={[
      { key: 'project_id', cfg: 'project_id', label: 'Project', required: true },
      { key: 'incident_date', cfg: 'incident_date', label: 'Incident Date', type: 'date', required: true },
      { key: 'type', cfg: 'type', label: 'Incident Type', type: 'select', options: ['Near Miss','First Aid','Medical Treatment','LTI','Fatality','Property Damage','Environmental'], required: true },
      { key: 'severity', cfg: 'severity', label: 'Severity', type: 'select', options: ['Low','Medium','High','Critical'], required: true },
      { key: 'location', cfg: 'location', label: 'Location' },
      { key: 'description', cfg: 'description', label: 'Description', type: 'textarea', full: true, required: true },
      { key: 'persons_involved', cfg: 'persons_involved', label: 'Persons Involved', type: 'textarea', full: true },
      { key: 'immediate_action', cfg: 'immediate_action', label: 'Immediate Action Taken', type: 'textarea', full: true },
    ]}
    columns={[
      { key: 'incident_date', label: 'Date', type: 'date' }, { key: 'project_id', label: 'Project' },
      { key: 'type', label: 'Type', type: 'status' }, { key: 'severity', label: 'Severity', type: 'status' },
      { key: 'description', label: 'Description' },
    ]}
  />
);

export const Documents = () => (
  <CrudPage overline="Document Management" title="Document Repository"
    subtitle="Searchable, version-controlled repository of all project documents."
    endpoint="documents"
    fields={[
      { key: 'doc_no', label: 'Document No' },
      { key: 'title', label: 'Title', full: true },
      { key: 'category', label: 'Category', type: 'select', options: ['Drawings','Contracts','POs','Invoices','Reports','ITP','Correspondence'] },
      { key: 'project', label: 'Project' },
    ]}
    columns={[
      { key: 'doc_no', label: 'No' }, { key: 'title', label: 'Title' },
      { key: 'category', label: 'Category' }, { key: 'project', label: 'Project' },
    ]}
  />
);

export const Billing = () => (
  <CrudPage overline="Billing & Contracts" title="Contract Register"
    subtitle="Client contracts, RA bills raised, retention and payment certificates."
    endpoint="contracts"
    fields={[
      { key: 'contract_no', label: 'Contract No', required: true },
      { key: 'client', label: 'Client', required: true },
      { key: 'type', label: 'Type', type: 'select', options: ['Lump Sum','Item Rate','Cost Plus','EPC Turnkey'], required: true },
      { key: 'value', label: 'Contract Value (₹)', type: 'number', required: true },
      { key: 'retention', label: 'Retention %', type: 'number' },
      { key: 'ld_clause', label: 'LD Clause', full: true },
    ]}
    columns={[
      { key: 'contract_no', label: 'Contract' }, { key: 'client', label: 'Client' },
      { key: 'type', label: 'Type' }, { key: 'value', label: 'Value', type: 'currency' },
      { key: 'retention', label: 'Retention %' },
    ]}
  />
);
